import { createReducer, on } from '@ngrx/store';
import { LocationModel } from '../../../locations/location.model';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import { DeviceModel } from '@models/device.model';

export declare interface LocationEditState {
  selectedLocationId: string;
  selectedLocation: LocationModel.LocationItem;

  selectedSpeaker: DeviceModel.LocationDeviceDocument;
  deviceState: DeviceModel.DeviceState;
  canDeactivate: boolean;
  isFormValid: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
}

export const initialState: LocationEditState = {
  selectedLocationId: null,
  selectedLocation: null,
  selectedSpeaker: null,
  deviceState: {
    testOngoing: false,
    testErrorMsg: '',
    inProgressNum: 0,
    testSuccess: false,
  },
  canDeactivate: true,
  isFormValid: true,
  isLoading: false,
  isSaving: false,
  isDeleting: false,
};

export const locationEditStateReducer = createReducer(
  initialState,
  on(LocationEditActions.resetToInitialState, (state) => {
    return {
      ...initialState,
    };
  }),
  on(LocationEditActions.resetDeviceState, state => {
    return {
      ...state,
      selectedSpeaker: null,
      deviceState: {
        testOngoing: false,
        testErrorMsg: '',
      },
    };
  }),
  on(LocationEditActions.setSelectedLocationId, (state, { selectedLocationId }) => {
    return {
      ...state,
      selectedLocationId,
    };
  }),
  on(LocationEditActions.setSelectedLocation, (state, { selectedLocation }) => {
    return {
      ...state,
      isLoading: false,
      selectedLocation,
    };
  }),
  on(LocationEditActions.updateSelectedLocation, (state, { updatedLocation }) => {
    // Only update if this location is currently selected
    if (state.selectedLocationId === updatedLocation._id) {
      return {
        ...state,
        selectedLocation: { ...state.selectedLocation, ...updatedLocation },
      };
    }
    return state;
  }),
  on(LocationEditActions.setSelectedSpeaker, (state, { selectedSpeaker }) => {
    return {
      ...state,
      selectedSpeaker,
    };
  }),
  on(LocationEditActions.setSelectedSpeakerById, (state, { speakerId }) => {
    const speaker = state.selectedLocation?.devices[speakerId];
    return {
      ...state,
      selectedSpeaker: speaker,
    };
  }),
  on(LocationEditActions.setDeviceState, (state, { deviceStateChange }) => {
    return {
      ...state,
      deviceState: {
        ...deviceStateChange,
      },
    };
  }),
  on(LocationEditActions.testSpeakerSuccess, (state) => {
    const inProgressNum = state.deviceState.inProgressNum - 1;
    return {
      ...state,
      deviceState: {
        inProgressNum,
        testOngoing: false,
        testSuccess: true,
      },
    };
  }),
  on(LocationEditActions.setCanDeactivate, (state, { canDeactivate }) => {
    return {
      ...state,
      canDeactivate,
    };
  }),
  on(LocationEditActions.deleteSpeakerSuccess, (state, { locationId, deviceId }) => {
    if (!state.selectedLocation || state.selectedLocationId !== locationId) {
      return state;
    }
    const updatedLocation = {
      ...state.selectedLocation,
      devices: { ...state.selectedLocation.devices },
    };
    delete updatedLocation.devices[deviceId];
    return {
      ...state,
      selectedLocation: updatedLocation,
      selectedSpeaker: null,
    };
  }),
  on(LocationEditActions.addSpeakerSuccess, (state, { locationId, deviceId, request, isEdit }) => {
    if (!state.selectedLocation || state.selectedLocationId !== locationId) {
      return state;
    }
    const updatedLocation = {
      ...state.selectedLocation,
      devices: {
        ...state.selectedLocation.devices,
        [deviceId]: {
          ...request,
          _id: deviceId,
        },
      },
    };
    return {
      ...state,
      selectedLocation: updatedLocation,
      selectedSpeaker: updatedLocation.devices[deviceId],
      deviceState: {
        ...state.deviceState,
        testOngoing: false,
        testSuccess: false,
        testErrorMsg: undefined,
      },
    };
  }),
  on(LocationEditActions.testSpeakerError, (state, { errorMsg }) => {
    if (state?.deviceState?.testSuccess === true) {
      return {
        ...state,
      };
    }
    const inProgressNum = state.deviceState.inProgressNum - 1;
    if (state?.deviceState?.testErrorMsg === 'Unauthorized: Wrong username or password') {
      errorMsg = state?.deviceState?.testErrorMsg;
    } else if (!!state?.deviceState?.testErrorMsg && errorMsg === 'The speaker is not connected. Try again'
      && state?.deviceState?.testErrorMsg !== 'The speaker is not connected. Try again') {
      errorMsg = state?.deviceState?.testErrorMsg;
    }
    return {
      ...state,
      deviceState: {
        ...state.deviceState,
        inProgressNum,
        testOngoing: inProgressNum === 0 ? false : true,
        testErrorMsg: errorMsg,
      },
    };
  }),
  on(LocationEditActions.testSip, (state) => {
    return {
      ...state,
      deviceState: {
        ...state.deviceState,
        testOngoing: true,
        testSuccess: false,
        testErrorMsg: '',
      },
    };
  }),
  on(LocationEditActions.testSipSuccess, (state) => {
    return {
      ...state,
      deviceState: {
        ...state.deviceState,
        testOngoing: false,
        testSuccess: true,
        testErrorMsg: '',
      },
    };
  }),
  on(LocationEditActions.testSipError, (state, { errorMsg }) => {
    return {
      ...state,
      deviceState: {
        ...state.deviceState,
        testOngoing: false,
        testSuccess: false,
        testErrorMsg: errorMsg,
      },
    };
  }),
  on(LocationEditActions.changeDetailsFormValidation, (state, { isFormValid }) => {
    return {
      ...state,
      isFormValid,
    };
  }),
  on(LocationEditActions.saveLocation, (state) => {
    return {
      ...state,
      isSaving: true,
    };
  }),
  on(LocationEditActions.addSpeaker, (state) => {
    return {
      ...state,
      isSaving: true,
    };
  }),
  on(LocationEditActions.deleteSpeaker, (state) => {
    return {
      ...state,
      isDeleting: true,
    };
  }),
  on(LocationEditActions.setIsSaving, (state, { isSaving }) => {
    return {
      ...state,
      isSaving,
    };
  }),
  on(LocationEditActions.setIsDeleting, (state, { isDeleting }) => {
    return {
      ...state,
      isDeleting,
    };
  }),
  on(LocationEditActions.resetTestState, (state) => {
    return {
      ...state,
      deviceState: {
        ...state.deviceState,
        testOngoing: false,
        testSuccess: false,
        testErrorMsg: '',
      },
    };
  }),
);

